import {gql, NetworkStatus} from '@apollo/client';
import React, {useState} from 'react';
import isEqual from 'react-fast-compare';

import BenchlingConfig from 'BenchlingConfig';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import ProcessRealtimeUpdateSubscriptionWrapper from 'processes/ProcessRealtimeUpdateSubscriptionWrapper';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';

import {
  ProcessFlowchartStructureQuery,
  ProcessFlowchartStructureQueryVariables,
} from './__generated/ProcessFlowchartStructureQuery';
import {
  ProcessTaskGroupFlowchartDataQuery,
  ProcessTaskGroupFlowchartDataQueryVariables,
} from './__generated/ProcessTaskGroupFlowchartDataQuery';
import {PROCESS_FLOWCHART_STRUCTURE_QUERY} from './LiveUpdatingTaskFlowchartWrapper';

type ProcessFlowchartStructure = ExtractByTypename<
  ProcessFlowchartStructureQuery['processFlowcharts'][number],
  'ProcessFlowchartNode'
>;
export const FLOWCHART_PROCESS_DATA_FRAGMENT = gql`
  fragment FlowchartProcessDataInfo on ProcessNode {
    id
    nodeConfig {
      id
    }
    taskStatusCounts(rootTaskId: $rootTaskId) {
      count
      status {
        id
        displayName
        statusType
      }
    }
    flowchartPredecessorNodes(rootTaskId: $rootTaskId) {
      id
    }
    flowchartNodeUrl(rootTaskId: $rootTaskId)
  }
`;

export const PROCESS_TASK_GROUP_FLOWCHART_DATA_QUERY = gql`
  query ProcessTaskGroupFlowchartDataQuery($processId: StrictString!, $rootTaskId: StrictString)
  @skipAudit
  @engComponent(name: WORKFLOWS) {
    process(id: $processId) {
      ... on ProcessNode {
        ...FlowchartProcessDataInfo
        childProcesses {
          ... on ProcessNode {
            ...FlowchartProcessDataInfo
          }
          ...InaccessibleInfoGraphQL
        }
        isMappingInProgressForFlowchartRootedAtProcess(rootTaskId: $rootTaskId)
      }

      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
  ${FLOWCHART_PROCESS_DATA_FRAGMENT}
`;

/**
 * This component executes two queries. The first query gets executed on
 * a polling loop, to ensure we sync with new server-side data that's been set asynchronously.
 * (A typical example: when a downstream task status is set to Invalid, the root task is set
 * to Cancelled. The new root status gets picked up during the poll.)
 *
 * Additionally, this component caches the query results. This prevents the ReactFlow library
 * from re-rendering the same flowchart with identical data.
 */
export function PollingFlowchartWrapper(props: {
  flowchartIds: Array<string>;
  processId: string;
  rootTaskId?: string;
  children: (
    flowcharts: Array<ProcessFlowchartStructure>,
    flowchartData: ProcessTaskGroupFlowchartDataQuery,
    forceRefetch: () => void
  ) => JSX.Element | null;
}): JSX.Element | null {
  const [flowchartCache, setFlowchartCache] = useState<Array<ProcessFlowchartStructure> | null>(null);
  const [flowchartDataCache, setFlowchartDataCache] = useState<ProcessTaskGroupFlowchartDataQuery | null>(
    null
  );

  const flowchartStructureResults = useBenchlingQuery<
    ProcessFlowchartStructureQuery,
    ProcessFlowchartStructureQueryVariables
  >(PROCESS_FLOWCHART_STRUCTURE_QUERY, {
    variables: {flowchartIds: props.flowchartIds},
  });

  // Do not use this value directly except for introspection. Use the cached value in state for rendering.
  const _flowchartDataResults = useBenchlingQuery<
    ProcessTaskGroupFlowchartDataQuery,
    ProcessTaskGroupFlowchartDataQueryVariables
  >(PROCESS_TASK_GROUP_FLOWCHART_DATA_QUERY, {
    variables: {processId: props.processId, rootTaskId: props.rootTaskId},
    // This query benefits tremendously from live updates. We'll get new tasks, new statuses, etc.
    pollInterval: BenchlingConfig.WORKFLOWS_FLOWCHART_VIEWER_POLL_INTERVAL_MS,
    renderCachedDataWhenRefetching: true,
  });

  if (
    flowchartStructureResults.type !== QueryResultType.SUCCESS ||
    (_flowchartDataResults.type !== QueryResultType.SUCCESS &&
      _flowchartDataResults.networkStatus === NetworkStatus.refetch)
  ) {
    return <CenteredHorizontalThrobber />;
  }

  if (
    _flowchartDataResults.type === QueryResultType.SUCCESS &&
    !isEqual(flowchartDataCache, _flowchartDataResults.data)
  ) {
    setFlowchartDataCache(_flowchartDataResults.data);
  }

  const _flowcharts = flowchartStructureResults.data.processFlowcharts.filter(
    (flowchart) => !isInaccessible(flowchart)
  ) as Array<ProcessFlowchartStructure>;
  if (!isEqual(flowchartCache, _flowcharts)) {
    setFlowchartCache(_flowcharts);
  }

  const forceRefetch = (): void => {
    setFlowchartCache(null);
    setFlowchartDataCache(null);
    _flowchartDataResults.client.resetStore();
  };

  if (flowchartDataCache == null || flowchartCache == null) {
    return <CenteredHorizontalThrobber />;
  }

  return props.children(flowchartCache, flowchartDataCache, forceRefetch);
}

export function NonPollingTaskGroupFlowchartWrapper(props: {
  flowchartIds: Array<string>;
  processId: string;
  rootTaskId?: string;
  refetchParent: () => Promise<unknown>;
  children: (
    flowcharts: Array<ProcessFlowchartStructure>,
    flowchartData: ProcessTaskGroupFlowchartDataQuery,
    forceRefetch: () => void
  ) => JSX.Element | null;
}): JSX.Element | null {
  const flowchartStructureResults = useBenchlingQuery<
    ProcessFlowchartStructureQuery,
    ProcessFlowchartStructureQueryVariables
  >(PROCESS_FLOWCHART_STRUCTURE_QUERY, {
    renderCachedDataWhenRefetching: true,
    variables: {flowchartIds: props.flowchartIds},
  });

  const flowchartDataResults = useBenchlingQuery<
    ProcessTaskGroupFlowchartDataQuery,
    ProcessTaskGroupFlowchartDataQueryVariables
  >(PROCESS_TASK_GROUP_FLOWCHART_DATA_QUERY, {
    variables: {processId: props.processId, rootTaskId: props.rootTaskId},
    renderCachedDataWhenRefetching: true,
    fetchPolicy: 'network-only',
  });

  if (
    flowchartStructureResults.type !== QueryResultType.SUCCESS ||
    flowchartDataResults.type !== QueryResultType.SUCCESS
  ) {
    return <CenteredHorizontalThrobber />;
  }

  const flowcharts = flowchartStructureResults.data.processFlowcharts.filter(
    (flowchart): flowchart is ProcessFlowchartStructure => !isInaccessible(flowchart)
  );

  return props.children(flowcharts, flowchartDataResults.data, () => {
    props.refetchParent();
    flowchartStructureResults.refetch();
    flowchartDataResults.refetch();
  });
}

export interface LiveUpdatingTaskGroupFlowchartWrapperProps {
  parentId: string;
  flowchartIds: Array<string>;
  processId: string;
  rootTaskId?: string;
  children: (
    flowcharts: Array<ProcessFlowchartStructure>,
    flowchartData: ProcessTaskGroupFlowchartDataQuery,
    forceRefetch: () => void
  ) => JSX.Element | null;
  refetch: () => Promise<unknown>;
}

export default function LiveUpdatingTaskGroupFlowchartWrapper(
  props: LiveUpdatingTaskGroupFlowchartWrapperProps
): JSX.Element {
  if (BenchlingConfig.WORKFLOWS_ENABLE_LIVE_UPDATING_UI) {
    return (
      <NonPollingTaskGroupFlowchartWrapper
        flowchartIds={props.flowchartIds}
        processId={props.processId}
        rootTaskId={props.rootTaskId}
        refetchParent={props.refetch}
      >
        {(flowcharts, flowchartData, forceRefetch) => {
          let hasIncompleteData = false;
          if (!isInaccessible(flowchartData.process)) {
            hasIncompleteData = flowchartData.process.isMappingInProgressForFlowchartRootedAtProcess;
          }

          return (
            <ProcessRealtimeUpdateSubscriptionWrapper
              apiId={props.parentId}
              refetch={forceRefetch}
              refetchOnMessagesFromSameClient={true}
              hasIncompleteDataShouldRefetch={hasIncompleteData}
            >
              {props.children(flowcharts, flowchartData, forceRefetch)}
            </ProcessRealtimeUpdateSubscriptionWrapper>
          );
        }}
      </NonPollingTaskGroupFlowchartWrapper>
    );
  } else {
    return (
      <PollingFlowchartWrapper
        flowchartIds={props.flowchartIds}
        processId={props.processId}
        rootTaskId={props.rootTaskId}
      >
        {(flowcharts, flowchartData, forceRefetch) => props.children(flowcharts, flowchartData, forceRefetch)}
      </PollingFlowchartWrapper>
    );
  }
}
